import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Search, ChevronDown, User2, BookOpen, LogOut } from 'lucide-react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationsBell } from './NotificationsBell'
import { useAuth } from '@/app/core/auth/AuthProvider'
import { primaryRoleLabel } from '@/app/core/auth/useRoles'

export function OpsTopbar() {
  const navigate = useNavigate()
  const { user, logout, mockMode } = useAuth()
  const [q, setQ] = useState('')

  const displayName = user?.name ?? 'User'
  const initials = user?.initials ?? 'U'
  const primaryRole = primaryRoleLabel(user?.roles ?? [])

  const handleSignOut = () => {
    if (mockMode) {
      toast.success('Signed out (mock)')
      return
    }
    logout()
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (q.trim()) navigate(`/products?q=${encodeURIComponent(q.trim())}`)
  }

  return (
    <header className="sticky top-0 z-30 h-[64px] flex items-center gap-4 px-8 bg-background/70 backdrop-blur-xl border-b border-border">
      <form onSubmit={submit} className="relative w-full max-w-[440px]">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search SKU, product, or lot…"
          className="h-10 w-full rounded-full border border-border bg-white/80 pl-10 pr-4 text-sm font-medium placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/10"
        />
      </form>

      <div className="flex-1" />

      <NotificationsBell />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Open profile menu"
            className="flex items-center gap-1.5 h-9 px-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <span className="w-[22px] h-[22px] rounded-full bg-white/[0.16] text-[11px] font-bold flex items-center justify-center">
              {initials}
            </span>
            <ChevronDown className="w-3.5 h-3.5 opacity-90" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[260px] p-0 overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border/60 bg-accent/30">
            <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground text-[13px] font-bold flex items-center justify-center">
              {initials}
            </span>
            <span className="leading-tight">
              <span className="block text-[14px] font-semibold text-foreground">{displayName}</span>
              <span className="block text-[11px] text-muted-foreground">{primaryRole}</span>
            </span>
          </div>
          <div className="p-1.5">
            <DropdownMenuItem asChild>
              <Link to="/profile" className="cursor-pointer flex items-center gap-2.5 w-full">
                <User2 className="w-4 h-4 text-muted-foreground" /> Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/help" className="cursor-pointer flex items-center gap-2.5 w-full">
                <BookOpen className="w-4 h-4 text-muted-foreground" /> Help / docs
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer flex items-center gap-2.5 text-destructive focus:text-destructive" onSelect={handleSignOut}>
              <LogOut className="w-4 h-4" /> Sign out
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
